import { CalendarPlus, Download, ExternalLink } from "lucide-react";
import {
  buildGoogleCalendarTemplateUrl,
  buildIcsDataUri,
} from "../lib/google-calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const rowClass =
  "flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700";

/**
 * Reusable, focus-trapped add-to-calendar modal (Radix Dialog: focus-in on open,
 * Tab-trap, Escape closes + restores focus to the trigger). The provider rows are
 * pure, identity-free deep links (Google TEMPLATE + a self-contained .ics data
 * URI) — NO Google API call. When a linked event already exists (hasLinkedEvent)
 * the dialog leads with a duplicate warning + an "Open in Google Calendar" link;
 * the phrasing keys on `invited`.
 *
 * The open state is CONTROLLED by the caller (surface) — never nest this inside a
 * DropdownMenu.Item (the menu unmounts on select and would drop focus before the
 * dialog mounts). See SessionCalendarControl.
 */
export function AddToCalendarDialog({
  open,
  onOpenChange,
  entry,
  filenameBase,
  hasLinkedEvent = false,
  openUrl = null,
  invited = false,
  timeLabel = null,
  onCloseAutoFocus,
}) {
  const googleUrl = entry ? buildGoogleCalendarTemplateUrl(entry) : null;
  const icsUri = entry ? buildIcsDataUri(entry) : null;
  const canAdd = Boolean(googleUrl || icsUri);

  const descriptionText = entry?.title
    ? `${entry.title}${timeLabel ? ` · ${timeLabel}` : ""}`
    : "Add this session to your calendar.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>Add to calendar</DialogTitle>
          <DialogDescription>{descriptionText}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-2">
          {hasLinkedEvent && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-200">
              <p>
                {invited
                  ? "This session already has a calendar event — you may already be on the guest list."
                  : "This session already has a calendar event."}
              </p>
              {openUrl && (
                <a
                  href={openUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 font-semibold text-amber-800 underline transition-colors hover:text-amber-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-amber-100 dark:hover:text-white"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  Open in Google Calendar
                </a>
              )}
            </div>
          )}

          {canAdd ? (
            <>
              {googleUrl && (
                <a
                  data-testid="add-to-calendar-google"
                  href={googleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={rowClass}
                >
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Add to Google Calendar
                </a>
              )}
              {icsUri && (
                <a
                  data-testid="add-to-calendar-ics"
                  href={icsUri}
                  download={`${filenameBase || "session"}.ics`}
                  className={rowClass}
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Download .ics
                </a>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Calendar details unavailable.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
